import { Show, createMemo, type JSX } from 'solid-js'
import type { CellScope, CollaboratorRowScope } from 'mormaii-web-presentation/collaborators'
import { STATUS_COLORS, STATUS_LABELS } from 'mormaii-web-presentation/domain'

import Css from './collaborator-cells.module.scss'
import { Icon } from '../icons'

/**
 * As celulas de uma linha de colaborador.
 *
 * DUAS telas desenham a mesma linha sobre o mesmo scope - a lista de Colaboradores
 * e Monitoramentos Compartilhados. As populacoes sao diferentes (la a base toda ou
 * a empresa, aqui so quem compartilhou com este profissional), mas a forma da linha
 * e a mesma, e por isso ela mora aqui e nao em nenhuma das duas.
 */

/**
 * A celula de uma coluna, pelo CAMPO dela.
 *
 * Era uma ordem fixa - as quatro fixas e depois as medicoes. Desde o B-42 a lista de
 * Colaboradores escolhe quais medicoes aparecem, em que ordem, e poe o indicador em
 * foco logo depois do nome. A tabela pede uma celula por campo e a identifica por ele:
 * mudar ou mover colunas nao recria as celulas que ficam.
 */
export function collaboratorCell(row: CollaboratorRowScope, field: string): JSX.Element {
    switch (field) {
        // Criticos: o ponto vermelho so quando ha, e o numero SEMPRE - inclusive
        // zero, que e a informacao de que esta pessoa esta bem.
        case 'criticos':
            return (
                <div class={Css.withDot}>
                    <Show when={row.criticalCount > 0}>
                        <span
                            class={Css.dot}
                            style={{ 'background-color': STATUS_COLORS.CRITICAL }}
                            title={STATUS_LABELS.CRITICAL}
                        />
                    </Show>
                    <span class={Css.number}>{row.criticalCount}</span>
                </div>
            )

        /*
            Um `<a>` de verdade, para o botao do meio abrir em outra aba.

            O endereco vem PRONTO e SELADO do scope: escrito a mao aqui ele sairia em
            texto claro, e a aplicacao recusa endereco que ela mesma nao produziu - o
            clique abriria o detalhe sem paciente. So o `#` e daqui, que e o que esta
            view sabe do roteamento por hash.
        */
        case 'nome':
            return (
                <a
                    href={`#${row.href}`}
                    onClick={(event) => {
                        event.preventDefault()
                        void row.onOpen()
                    }}
                    class={Css.name}
                >
                    {row.name}
                </a>
            )

        case 'origem':
            return (
                <div class={Css.withDot}>
                    <Show when={row.sourceIcon}>
                        <Icon name={row.sourceIcon} size={18} class={Css.source} />
                    </Show>
                    <span>{row.sourceTitle || '-'}</span>
                </div>
            )

        case 'atualizado':
            return <span class={Css.number}>{row.updatedAt}</span>

        default:
            return <MeasurementOf row={row} field={field} />
    }
}

/**
 * A medicao de uma coluna, achada pelo campo NUM MEMO.
 *
 * A linha reaproveitada recebe a cada recarga um array NOVO de celulas - com as mesmas
 * celulas dentro. Achada fora do memo, a troca do array refaria o DOM de toda celula de
 * medicao; no memo, a mesma celula volta, e so o que mudou nela e redesenhado.
 */
function MeasurementOf(props: { row: CollaboratorRowScope; field: string }): JSX.Element {
    const cell = createMemo(() => props.row.cells.find((candidate) => `m${candidate.identity}` === props.field))
    return (
        <Show when={cell()} fallback={<span>-</span>}>
            {(scope) => <Measurement scope={scope()} />}
        </Show>
    )
}

/**
 * Uma medicao: o ponto da faixa vem ANTES do valor.
 *
 * Com `href`, a celula e um `<a>` de verdade, como o nome - hoje so a de sono, que abre
 * o Relatorio de Sono da pessoa. O endereco vem SELADO do scope, pelo mesmo motivo do
 * nome; daqui e so o `#`.
 */
function Measurement(props: { scope: CellScope }): JSX.Element {
    const content = () => (
        <>
            <Show when={props.scope.statusName}>
                <span
                    class={Css.dot}
                    style={{ 'background-color': props.scope.statusColor }}
                    title={props.scope.statusName}
                />
            </Show>
            <span class={Css.number}>{props.scope.value}</span>
        </>
    )

    return (
        <Show when={props.scope.href} fallback={<div class={Css.withDot}>{content()}</div>}>
            <a
                href={`#${props.scope.href}`}
                onClick={(event) => {
                    event.preventDefault()
                    void props.scope.onOpen()
                }}
                class={`${Css.withDot} ${Css.link}`}
                title="Abrir o Relatório de Sono"
            >
                {content()}
            </a>
        </Show>
    )
}
